import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import moment from "moment-timezone";
import {uniq} from "lodash";
import {ConfigProvider, message} from "antd";
import {OrderChangesHistoryModal} from "./OrderChangesHistoryModal";
import {OrderImagesModal} from "./OrderImagesModal";
import {ORDERS_PER_PAGE, OrderTableColumns} from "./consts";
import columns from "./columns";
import {OrdersStatisticsLabel} from "./OrdersStatisticsLabel";
import {GenericModal, OrderStatus, PageTitleHeader, RoundedTable, StyledButton} from "../../components";
import {useRequest} from "../../utils/hooks";
import {BUDGET_LOAD_TRANSACTION} from "../../orders/consts";
import {HttpClient} from "../../http/HttpClient";
import {GoogleAnalytics} from "../../GoogleAnalytics";
import {AppContext} from "../../AppContext";
import "./admin-orders.css";
import {Images} from "../../images/Images";
import {EventBus} from "../../bus/EventBus";
import {StringBuilder} from "../../AppUtils.js";
import {DeclineOrderModal} from "../../event/DeclineOrderModal/DeclineOrderModal.jsx";
import {openWindow} from "../../http/WindowOpener.jsx";
import {TableDateSearchContext} from "../Context/TableDateSearchContext.jsx";
import {ChangeOrderDeliveryShippingModal} from "../../event/ChangeOrderDeliveryShippingModal/ChangeOrderDeliveryShippingModal.jsx";
import {UpdateOrderStatusModal} from "./UpdateOrderModal/index.js";
import {transformOrders, filterOrdersBySearchInput} from "./utils.jsx";
import {TableDateRangeTable} from "../../components/OrdersDateRangeTable/TableDateRangeTable.jsx";
import {
    OrderShortcutFilterValues,
    ORDER_SHORTCUT_SUMMARY_ROOM,
    ORDER_SHORTCUT_SUMMARY_UPDATED_EVENT,
    OrdersShortcuts,
    OpenIssuesShortcut,
    RatedShortcut
} from "../../event/consts.js";
import {updateBudgetTransactionAdminComment} from "../../api/budget/adminBudget.js";
import {
    createOrderIssueLog,
    updateOrderIssueLog,
    updateOrder,
    sendReminderToProvider as sendReminderToProviderApi,
    reviewNoDriver,
    reviewDecline,
    reviewBadRating
} from "../../api/orders/adminOrders.js";
import {
    getToastMessageToExcelOrders,
    generateAdminOrderTableActions,
    generateProviderReminderOperationActions
} from "./utils.jsx";
import {ProductBookingAsClientModal} from "../../marketplace/MarketplaceBooking/ProductBooking/ProductBookingAsClient/ProductBookingAsClientModal.jsx";
import {shippingProvidersOptions, shippingProvidersOptionsToName} from "../upload/consts.jsx";
import {IssueModal} from "../../event/IssueModal/IssueModal.jsx";
import {OrderIssueLogsModal} from "../../event/IssueModal/OrderIssueLogsModal.jsx";
import {useListenToSocket} from "../../components/socket/useListenToSocket.jsx";

export const AdminOrders = ({history}) => {
    const {me, services} = useContext(AppContext);
    const [innerOrders, setInnerOrders] = useState([]);
    const [searchInput, setSearchInput] = useState(null);

    const {searchDate, searchByCreated, searchStatuses, formValues} = useContext(TableDateSearchContext);

    const [historyOrder, setHistoryOrder] = useState(null);
    const [imagesOrder, setImagesOrder] = useState(null);
    const [orderImagesModalVisible, setOrderImagesModalVisible] = useState(false);
    const [updateOrderStatusInfo, setUpdateOrderStatusInfo] = useState(null);
    const [budgetLoadTransaction, setBudgetLoadTransaction] = useState();
    const [eventActionModal, setEventActionModal] = useState(null);
    const [eventActionModalText, setEventActionModalText] = useState(null);
    const [declineOrder, setDeclineOrder] = useState(null);
    const [changeDeliveryProviderOrder, setChangeDeliveryProviderOrder] = useState(null);
    const [visibleCreateOrderAsClient, setVisibleCreateOrderAsClient] = useState(false);
    const [issueModalOrder, setIssueModalOrder] = useState(null);

    const openIssuedOrders = formValues?.selectedOption === OrderShortcutFilterValues.OPEN_ISSUES;
    const ratedOrders = formValues?.selectedOption === OrderShortcutFilterValues.RATED;

    const baseShortcutOptions = useMemo(() => [...OrdersShortcuts, OpenIssuesShortcut, RatedShortcut], []);
    const [summary, _loadingSummary, _errSummary, fetchSummary] = useRequest(
        "/admin/api/orderShortcutSummary?key=order-management",
        "get",
        null,
        [],
        true
    );

    useListenToSocket({
        eventName: ORDER_SHORTCUT_SUMMARY_UPDATED_EVENT,
        onUpdate: fetchSummary,
        room: ORDER_SHORTCUT_SUMMARY_ROOM
    });

    const shortcutSearchOptions = useMemo(
        () =>
            baseShortcutOptions.map(opt => ({
                ...opt,
                notificationCount: summary?.summary?.find(s => s.key === opt.key)?.count ?? 0
            })),
        [baseShortcutOptions, summary]
    );

    const [orders, isLoadingOrders, _isErrorOnLoadingOrders, fetchOrders] = useRequest(
        "/admin/api/orders",
        "POST",
        {
            startTime: searchDate[0].valueOf(),
            endTime: searchDate[1].valueOf(),
            byCreatedAt: searchByCreated,
            ...(searchStatuses?.length ? {statuses: searchStatuses} : {}),
            ...(openIssuedOrders ? {openIssuedOrders: true} : {}),
            ...(ratedOrders ? {ratedOrders: true} : {})
        },
        [searchDate, searchByCreated, searchStatuses, openIssuedOrders, ratedOrders],
        searchDate?.length,
        transformOrders
    );

    const sendReminderToProvider = useCallback(
        async order => {
            try {
                const {error} = await sendReminderToProviderApi(order);
                if (!error) {
                    fetchOrders(); // Refresh to show updated count
                }
            } catch (e) {
                message.error(e.message, 3);
            }
        },
        [fetchOrders]
    );

    const operationActions = useMemo(
        () =>
            generateProviderReminderOperationActions(
                me,
                sendReminderToProvider,
                setDeclineOrder,
                setIssueModalOrder,
                order => onNoDriverIssueModalOpen(order),
                order => onDeclineReview(order),
                order => onBadRatingIssueModalOpen(order)
            ),
        [me, sendReminderToProvider, setDeclineOrder, setIssueModalOrder]
    );

    const includeBugdet = useMemo(
        () => !formValues?.selectedOption || formValues?.selectedOption === OrderShortcutFilterValues.TODAY_ORDERS,
        [formValues?.selectedOption]
    );

    const [budgetLogs, isLoadingBudgetTransactions] = useRequest(
        `/admin/api/getLoadedBudgetTransactionBetweenDates/${searchDate[0].valueOf()}/${searchDate[1].valueOf()}?enrichCompanyDetails=true`,
        "get",
        null,
        [includeBugdet, searchDate],
        searchDate?.length > 1 && includeBugdet
    );
    const [deliveryProviders] = useRequest("/admin/api/deliveryProviders");

    useEffect(() => {
        setBudgetLoadTransaction(budgetLogs);
    }, [budgetLogs]);

    useEffect(() => {
        !includeBugdet && setBudgetLoadTransaction([]);
    }, [includeBugdet]);

    useEffect(() => {
        if (orders?.error) {
            message.error(
                new StringBuilder().append("שגיאה בעת טעינת הזמנות מוצרים").append(`(${orders?.error})`).toString(),
                10
            );
        }
    }, [orders?.error]);

    const orderingCompanies = useMemo(() => orders?.length && uniq(orders.map(({companyId}) => companyId)), [orders]);

    const [companiesStats] = useRequest(
        "/admin/api/statistics/getCompaniesOrderStatistics",
        "post",
        orderingCompanies,
        [orderingCompanies],
        orderingCompanies?.length
    );

    const [systemUsers] = useRequest("/admin/api/users/systemStaffUsers");

    const ordersWithBudgetLoads = useMemo(() => {
        return [...(innerOrders ?? []), ...(budgetLoadTransaction ?? [])];
    }, [innerOrders, budgetLoadTransaction]);

    const onEditOrder = useCallback(order => {
        openWindow(`/admin/events/${order.eventId}/edit`);
    }, []);

    const onChangeOrderStatus = useCallback(
        async (event, status, toRemove) => {
            try {
                let showSuccess = true;
                let shouldSyncAllOrders = true;
                if (toRemove) {
                    await HttpClient.post(`/admin/api/orders/${event.eventId}/changeStatus/${status}`, {
                        remove: toRemove,
                        updatedAt: event.updatedAt
                    });
                } else {
                    switch (status) {
                        case OrderStatus.accepted: {
                            const {error, status} = await HttpClient.safePost(`/api/events/${event.eventId}/acceptV2`, {
                                updatedAt: event.updatedAt
                            });

                            if (status >= 400) {
                                const errorMessage = `Failed to accept event ${event.eventId}. Please contact support.`;
                                EventBus.triggerError(
                                    "server-error",
                                    {content: {description: errorMessage}},
                                    error.message
                                );
                                showSuccess = false;
                            } else if (error) {
                                setEventActionModalText(error);
                                setEventActionModal(event);
                                showSuccess = false;
                            }
                            GoogleAnalytics.event("Session Proposal", "Accept", event.eventId);
                            break;
                        }
                        case OrderStatus.declined: {
                            if (me?.features?.declineReason) {
                                setDeclineOrder(event);
                                showSuccess = false;
                                shouldSyncAllOrders = false;
                            } else {
                                await HttpClient.post(`/api/events/${event.eventId}/declineV2`);
                            }
                            break;
                        }
                        case OrderStatus.delivered: {
                            await HttpClient.post(`/api/events/${event.eventId}/delivered`);
                            break;
                        }
                        case OrderStatus.cancelled: {
                            await HttpClient.post(
                                `/admin/api/orders/${event.eventId}/cancel?updatedAt=${event.updatedAt}`
                            );
                            getToastMessageToExcelOrders(status, event);
                            break;
                        }
                        default: {
                            await HttpClient.post(`/admin/api/orders/${event.eventId}/changeStatus/${status}`, {
                                remove: toRemove,
                                updatedAt: event.updatedAt
                            });
                            break;
                        }
                    }
                }

                if (showSuccess) {
                    message.success("ההזמנה השתנתה בהצלחה.");
                }

                if (shouldSyncAllOrders) {
                    fetchOrders();
                }
            } catch (e) {
                message.error(e.message, 3);
            }
            setUpdateOrderStatusInfo(null);
        },
        [fetchOrders, me?.features?.declineReason]
    );

    const declineOffer = useCallback(
        async ({declineReason, declineComment}) => {
            try {
                const {error} = await HttpClient.post(`/api/events/${declineOrder.eventId}/declineV2`, {
                    declineReason,
                    declineComment,
                    updatedAt: declineOrder.updatedAt
                });

                if (error) {
                    message.error(error, 3);
                } else {
                    message.success("ההזמנה השתנתה בהצלחה.");
                    getToastMessageToExcelOrders(OrderStatus.declined, declineOrder);
                }

                fetchOrders();
            } catch (e) {
                message.error(e.message, 3);
            }
            setDeclineOrder(null);
        },
        [declineOrder, fetchOrders]
    );

    useEffect(() => {
        if (orders?.length) {
            setInnerOrders(orders);
        } else {
            setInnerOrders([]);
        }
    }, [orders]);

    const filteredTransactions = useMemo(
        () => filterOrdersBySearchInput(ordersWithBudgetLoads, searchInput),
        [ordersWithBudgetLoads, searchInput]
    );

    const ordersForStatistics = useMemo(
        () =>
            filteredTransactions?.filter(
                ({status}) => ![OrderStatus.cancelled, OrderStatus.declined, OrderStatus.cancelPending].includes(status)
            ),
        [filteredTransactions]
    );

    const innerUpdateOrder = useCallback((newOrderInfo, budgetTransaction = false) => {
        const updateFunction = prev => {
            const tempPrevTransactions = [...prev];

            if (!newOrderInfo?.parentOrderId) {
                const replaceItemIndex = tempPrevTransactions.findIndex(
                    ({eventId}) => newOrderInfo.eventId === eventId
                );
                tempPrevTransactions[replaceItemIndex] = {
                    ...tempPrevTransactions[replaceItemIndex],
                    ...newOrderInfo
                };
                return tempPrevTransactions;
            } else {
                return tempPrevTransactions.map(order => {
                    if (order.eventId === newOrderInfo?.parentOrderId) {
                        order.subOrders = order.subOrders.map(subOrder =>
                            subOrder.eventId === newOrderInfo.eventId ? {...subOrder, ...newOrderInfo} : subOrder
                        );
                    }

                    return order;
                });
            }
        };

        budgetTransaction ? setBudgetLoadTransaction(updateFunction) : setInnerOrders(updateFunction);
    }, []);

    const loadOrderImages = useCallback(async order => {
        const images = await HttpClient.safeGet(`/admin/api/orderImages/order/${order.eventId}`);

        if (images?.error) {
            message.error("שגיאה בטעינת התמונות");
            return;
        }

        setImagesOrder({
            productId: order.productId,
            providerId: order.providerId,
            orderId: order.eventId,
            images
        });
        setOrderImagesModalVisible(true);
    }, []);

    const onChangeCSM = useCallback(
        async (selectedCsm, currentCSMs = [], eventId, isBudgetLoadTransaction, updatedAt, parentOrderId = null) => {
            const csmExists = currentCSMs.some(({userId}) => userId === selectedCsm.userId);
            const assignedCSMs = csmExists
                ? currentCSMs.filter(({userId}) => userId !== selectedCsm.userId)
                : [...currentCSMs, selectedCsm];
            const toUpdate = {
                eventId,
                parentOrderId,
                assignedCSMs
            };

            const {error, status} =
                (await (!isBudgetLoadTransaction
                    ? HttpClient.safePost(`/admin/api/transactions/updateOrder?updatedAt=${updatedAt}`, {
                          order: toUpdate,
                          whoToSendUpdateEmail: "none"
                      })
                    : HttpClient.safePost(`/admin/api/updateBudgetTransactionAssignCSMs/${eventId}`, assignedCSMs))) ??
                {};

            if (status === 409) {
                message.error(error);
                return;
            }

            if (error) {
                message.error(`שגיאה בעת עדכון מנהלת לקוח | מזהה הזמנה ${eventId}`);
                return;
            }

            fetchOrders();
            innerUpdateOrder(toUpdate, isBudgetLoadTransaction);
        },
        [innerUpdateOrder]
    );

    const onChangePartner = useCallback(
        async (selectedPartner, currentPartners = [], eventId, updatedAt, parentOrderId = null) => {
            const partnerExists = currentPartners.some(({userId}) => userId === selectedPartner.userId);

            const assignedPartners = partnerExists
                ? currentPartners.filter(({userId}) => userId !== selectedPartner.userId)
                : [...currentPartners, selectedPartner];

            const toUpdate = {
                eventId,
                parentOrderId,
                assignedPartners
            };

            const {error, status} = await HttpClient.safePost(
                `/admin/api/transactions/updateOrder?updatedAt=${updatedAt}`,
                {
                    order: toUpdate,
                    whoToSendUpdateEmail: "none"
                }
            );

            if (status === 409) {
                message.error(error);
                return;
            }

            if (error) {
                message.error(`שגיאה בעת עדכון מנהל/ת שותפים | מזהה הזמנה ${eventId}`);
                return;
            }

            fetchOrders();
            innerUpdateOrder(toUpdate);
        },
        [innerUpdateOrder]
    );

    const saveOrderImages = useCallback(async (orderId, productId, providerId, images) => {
        const {error} = await HttpClient.safePost("/admin/api/orderImages", {
            images,
            orderId,
            productId,
            providerId
        });
        if (error) {
            message.error(error.message);
            return;
        }

        message.success("התמונות נשמרו בהצלחה");
        setOrderImagesModalVisible(false);
    }, []);

    const chargeCreditCard = useCallback(
        async eventId => {
            const res = await HttpClient.safePost(`/admin/api/orders/chargeCreditCard/${eventId}`);
            if (res.error) {
                message.error(res.error);
                return;
            }

            innerUpdateOrder(res);
            message.success("חיוב בהצלחה");
        },
        [innerUpdateOrder]
    );

    const onCommentSave = useCallback(
        async (order, commentText) => {
            const adminComment = commentText
                ? {
                      content: commentText,
                      author: me.firstName,
                      lastUpdate: Date.now()
                  }
                : null;

            const toUpdate = {
                eventId: order.eventId,
                adminComment
            };

            const {error} = order.isBudgetTransaction
                ? await updateBudgetTransactionAdminComment(
                      order,
                      adminComment,
                      "שגיאה, ההערה על ההזמנה לא נשמרה",
                      "ההערה על ההזמנה נשמרה בהצלחה"
                  )
                : await updateOrder(
                      toUpdate,
                      "none",
                      order.updatedAt,
                      "שגיאה, ההערה על ההזמנה לא נשמרה",
                      "ההערה על ההזמנה נשמרה בהצלחה"
                  );

            if (error) {
                return;
            }

            innerUpdateOrder(
                {
                    eventId: order.eventId,
                    adminComment
                },
                order.paymentType === BUDGET_LOAD_TRANSACTION
            );

            fetchOrders();
        },
        [fetchOrders, innerUpdateOrder, me.firstName]
    );

    const onSyncOrder = useCallback(
        async order => {
            const res = await HttpClient.safePost(`/api/events/${order?.eventId}/deliveryProvider/sync`, {});

            if (res?.error) {
                message.error(res.error);
                return;
            }
            message.success("הזמנה סונכרנה מול חברת המשלוחים בהצלחה");
            fetchOrders();
        },
        [fetchOrders]
    );

    const onSaveIssue = useCallback(
        async issue => {
            if (issue.logId) {
                await updateOrderIssueLog({...issue, eventId: issueModalOrder.eventId});
            } else {
                await createOrderIssueLog({...issue, eventId: issueModalOrder.eventId});
            }
            setIssueModalOrder(null);
            fetchOrders();
        },
        [fetchOrders, issueModalOrder]
    );

    const onNoDriverIssueModalOpen = useCallback(
        async order => {
            setIssueModalOrder(order);
            await reviewNoDriver(order);
            fetchOrders();
        },
        [fetchOrders, setIssueModalOrder]
    );

    const onBadRatingIssueModalOpen = useCallback(
        async order => {
            setIssueModalOrder(order);
            await reviewBadRating(order);
            fetchOrders();
        },
        [fetchOrders, setIssueModalOrder]
    );

    const onDeclineReview = useCallback(
        async order => {
            await reviewDecline(order);
            fetchOrders();
        },
        [fetchOrders]
    );

    return (
        <div
            className="admin-orders-page"
            style={{
                display: "flex",
                justifyContent: "center",
                margin: 10,
                marginBottom: 40
            }}>
            <UpdateOrderStatusModal
                setUpdateOrderStatusInfo={setUpdateOrderStatusInfo}
                updateOrderStatusInfo={updateOrderStatusInfo}
                onChangeOrderStatus={onChangeOrderStatus}
            />

            <DeclineOrderModal visible={!!declineOrder} onSave={declineOffer} onClose={() => setDeclineOrder(null)} />

            <ChangeOrderDeliveryShippingModal
                onSave={fetchOrders}
                deliveryProviders={[
                    ...(deliveryProviders ?? []),
                    {
                        name: shippingProvidersOptionsToName[shippingProvidersOptions.regular],
                        deliveryProviderId: shippingProvidersOptions.regular
                    }
                ]}
                visible={!!changeDeliveryProviderOrder}
                orderId={changeDeliveryProviderOrder?.eventId}
                onClose={() => setChangeDeliveryProviderOrder(null)}
            />

            <ProductBookingAsClientModal
                visible={visibleCreateOrderAsClient}
                onClose={() => setVisibleCreateOrderAsClient(false)}
            />

            <ConfigProvider direction="rtl">
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        maxWidth: 2000,
                        width: "100%"
                    }}>
                    <PageTitleHeader showBack={false}>עסקאות</PageTitleHeader>
                    <TableDateRangeTable
                        data={filteredTransactions}
                        onInputSearch={setSearchInput}
                        shortcutSearchOptions={shortcutSearchOptions}
                        statisticsLabel={
                            <div className="admin-orders-top-bar-left-side">
                                <OrdersStatisticsLabel orders={ordersForStatistics} />
                                <StyledButton
                                    className="create-order-btn"
                                    onClick={() => setVisibleCreateOrderAsClient(true)}>
                                    יצירת הזמנה
                                </StyledButton>
                            </div>
                        }
                        isLoadingOrderTable={isLoadingOrders || isLoadingBudgetTransactions}
                        enforceMaxDateRange={true}
                        maxDateRangeUnit="week"
                        actions={generateAdminOrderTableActions(
                            me,
                            onEditOrder,
                            (order, status, toRemove) =>
                                setUpdateOrderStatusInfo({
                                    event: order,
                                    status,
                                    toRemove
                                }),
                            order => setHistoryOrder(order),
                            order => loadOrderImages(order),
                            chargeCreditCard,
                            onSyncOrder,
                            order => setChangeDeliveryProviderOrder(order),
                            order => setIssueModalOrder(order)
                        )}
                        columns={columns(
                            me,
                            services,
                            onEditOrder,
                            (order, status, toRemove) =>
                                setUpdateOrderStatusInfo({
                                    event: order,
                                    status,
                                    toRemove
                                }),
                            innerUpdateOrder,
                            systemUsers,
                            onChangeCSM,
                            onChangePartner,
                            order => setHistoryOrder(order),
                            order => loadOrderImages(order),
                            true,
                            companiesStats,
                            chargeCreditCard,
                            onCommentSave,
                            deliveryProviders,
                            null,
                            onSyncOrder,
                            order => setChangeDeliveryProviderOrder(order),
                            operationActions
                        )}
                        expandable={{
                            expandedRowRender: record => (
                                <div>
                                    <RoundedTable
                                        className="admin-orders-table"
                                        rowKey="eventId"
                                        pagination={false}
                                        scroll={{x: 1400}}
                                        // loading={isLoadingOrders || isLoadingBudgetTransactions}
                                        dataSource={record.subOrders}
                                        // rowClassName={getRowClass}
                                        columns={columns(
                                            me,
                                            services,
                                            onEditOrder,
                                            (order, status, toRemove) =>
                                                setUpdateOrderStatusInfo({
                                                    event: order,
                                                    status,
                                                    toRemove
                                                }),
                                            innerUpdateOrder,
                                            systemUsers,
                                            onChangeCSM,
                                            onChangePartner,
                                            order => setHistoryOrder(order),
                                            order => loadOrderImages(order),
                                            true,
                                            companiesStats,
                                            chargeCreditCard,
                                            onCommentSave,
                                            deliveryProviders,
                                            null,
                                            onSyncOrder,
                                            order => setChangeDeliveryProviderOrder(order)
                                        )}
                                    />
                                </div>
                            ),
                            rowExpandable: record => !!record?.subOrders?.length
                        }}
                        pagination={{
                            position: ["bottomCenter"],
                            showSizeChanger: false,
                            defaultPageSize: ORDERS_PER_PAGE,
                            showTotal: total => <span>סה״כ {total}</span>
                        }}
                        tableClassName="admin-orders-table"
                        hideColumns={[
                            OrderTableColumns.assignedPartners,
                            OrderTableColumns.tip,
                            OrderTableColumns.providerDiscount,
                            OrderTableColumns.deliveryProviderDiscount,
                            OrderTableColumns.discount,
                            OrderTableColumns.actions
                        ]}
                    />
                </div>

                <OrderChangesHistoryModal
                    visible={!!historyOrder}
                    orderId={historyOrder?.eventId}
                    onClose={() => setHistoryOrder(null)}
                    isAdmin={true}
                />
                <OrderImagesModal
                    visible={orderImagesModalVisible}
                    orderImages={imagesOrder?.images ?? []}
                    onClose={() => {
                        setOrderImagesModalVisible(false);
                        setImagesOrder(null);
                    }}
                    onSave={images =>
                        saveOrderImages(imagesOrder?.orderId, imagesOrder?.productId, imagesOrder?.providerId, images)
                    }
                />
                <GenericModal
                    title="שימו לב!"
                    className="admin-coupons-are-you-sure-modal"
                    text={[eventActionModalText]}
                    image={Images.areYouSure}
                    closeable={true}
                    visible={!!eventActionModal}
                    onClose={() => setEventActionModal(null)}
                    options={[
                        {
                            id: "refresh",
                            onClick: () => {
                                fetchOrders();
                                setEventActionModal(null);
                                setEventActionModalText(null);
                            },
                            text: "רענן"
                        }
                    ]}
                />
                <OrderIssueLogsModal
                    visible={!!issueModalOrder}
                    onClose={() => setIssueModalOrder(null)}
                    onSave={onSaveIssue}
                    order={issueModalOrder}
                />
            </ConfigProvider>
        </div>
    );
};
